// =============================================================================
// src/data/data-provenance.ts — Proveniencia de dados por superficie (puro).
//
// Proveniencia: criado na reforma institucional 2026-06 (decisao D3 do plano):
//   toda superficie de dados ganha um rodape discreto "Fonte: ... · atualizado
//   em ..." e as escalas proprietarias (Pertencimento, Sentimento) ganham nota
//   explicativa — credibilidade via citacao institucional, SEM carimbos de
//   "simulado" (precedente: rep-dim-desc da Reputacao explica cada dimensao).
//
// PROVENANCE_AS_OF e uma const deterministica (sem new Date()): e a data
//   canonica do mundo-mock, alinhada ao BRIEFING_DATE_LABEL do Briefing.
// =============================================================================

import type { SectionId } from '../app/types';

/** Data canonica de atualizacao exibida nos rodapes (mundo-mock, deterministica). */
export const PROVENANCE_AS_OF = '09 jun 2026';

/** Superficies que exibem rodape de proveniencia (8 secoes de dados + rotas).
 *  Cinema fica fora: e modo de apresentacao, nao superficie de dados. */
export type ProvenanceSurfaceId = Exclude<SectionId, 'cinema'> | 'briefing' | 'explore' | 'grafo' | 'analytics';

/** Fontes institucionais citadas por superficie. */
export const PROVENANCE_BY_SURFACE: Record<ProvenanceSurfaceId, readonly string[]> = {
  'mapa-eleitoral': ['TSE', 'IBGE Censo 2022'],
  'perfil-regiao': ['TSE', 'IBGE Censo 2022', 'Atlas Brasil'],
  comparador: ['TSE 2014–2024'],
  demografica: ['TSE', 'IBGE Censo 2022'],
  camadas: ['TSE', 'IBGE', 'INEP/IDEB'],
  negocios: ['Receita Federal', 'JUCEA', 'TSE'],
  impostometro: ['SEFAZ-AM', 'Tesouro Nacional'],
  monitoramento: ['TSE', 'monitoramento próprio'],
  // Reforma 2026-07-28: os KPIs do briefing passaram a vir do social_listening
  // (IBGE saiu junto com o KPI de eleitorado); o mini-mapa segue TSE.
  briefing: ['TSE', 'Social Listening AtalaIA'],
  explore: ['TSE', 'IBGE Censo 2022'],
  grafo: ['mapeamento próprio', 'TSE'],
  analytics: ['monitoramento próprio', 'mídias online', 'redes sociais'],
};

/**
 * Rodape de proveniencia de uma superficie: fonte(s) + data canonica.
 *
 * @example
 *   buildProvenanceFooterMarkup('negocios');
 *   // <div class="u-provenance-foot">… Fonte: Receita Federal · JUCEA · TSE · atualizado 09 jun 2026</div>
 */
export function buildProvenanceFooterMarkup(surface: ProvenanceSurfaceId): string {
  const sources = PROVENANCE_BY_SURFACE[surface].join(' · ');
  return `<div class="u-provenance-foot"><i class="ti ti-database" aria-hidden="true"></i> Fonte: ${sources} · atualizado ${PROVENANCE_AS_OF}</div>`;
}

/** Origem resolvida em runtime do dado da Explorar (Hasura ao vivo vs base local). */
export type ProvenanceResolvedSource = 'live' | 'static';

/**
 * Rodape da Explorar que NOMEIA a origem resolvida em runtime: "AtalaIA · ao vivo"
 * quando o dado veio do Hasura, ou "base local" no fallback estatico. Reforma de
 * runtime 2026-06 — alimentado por exploreLiveSource() no card.
 *
 * @example
 *   buildExploreProvenanceFooterMarkup('live');
 *   // <div class="u-provenance-foot">… Fonte: TSE · IBGE Censo 2022 · AtalaIA · ao vivo · atualizado 09 jun 2026</div>
 */
export function buildExploreProvenanceFooterMarkup(source: ProvenanceResolvedSource): string {
  const base = PROVENANCE_BY_SURFACE.explore.join(' · ');
  const origin = source === 'live' ? 'AtalaIA · ao vivo' : 'base local';
  return `<div class="u-provenance-foot"><i class="ti ti-database" aria-hidden="true"></i> Fonte: ${base} · ${origin} · atualizado ${PROVENANCE_AS_OF}</div>`;
}

/**
 * Sufixo "· atualizado ..." dos rodapes com dado vivo. Rotulo nulo SOME em vez
 * de virar data inventada — a const PROVENANCE_AS_OF descreve o mundo-mock das 8
 * secoes TSE/IBGE e nao tem o que dizer sobre quando a coleta rodou.
 */
function provenanceUpdateSuffix(updatedAtLabel: string | null): string {
  return updatedAtLabel ? ` · atualizado ${updatedAtLabel}` : '';
}

/**
 * Rodape do Analytics que NOMEIA a origem resolvida em runtime (precedente
 * exato: buildExploreProvenanceFooterMarkup) e recebe o INSTANTE da ultima
 * coleta: "Social Listening · ao vivo" quando o social_listening respondeu via
 * Hasura, "base demonstrativa" no fallback mock.
 *
 * 2026-09-20: a data deixou de ser a const congelada. O bundle publicado em
 * 15/09/2026 exibia "atualizado 09 jun 2026" porque nada no sistema regenerava
 * aquele literal. Agora vem de analyticsProvenanceUpdateLabel(getAnalyticsFreshness()).
 *
 * @example
 *   buildAnalyticsProvenanceFooterMarkup('live', '19 set 2026, 23h20');
 *   // <div class="u-provenance-foot">… · Social Listening · ao vivo · atualizado 19 set 2026, 23h20</div>
 *   buildAnalyticsProvenanceFooterMarkup('static', null);
 *   // <div class="u-provenance-foot">… · base demonstrativa</div>
 */
export function buildAnalyticsProvenanceFooterMarkup(
  source: ProvenanceResolvedSource,
  updatedAtLabel: string | null,
): string {
  const base = PROVENANCE_BY_SURFACE.analytics.join(' · ');
  const origin = source === 'live' ? 'Social Listening · ao vivo' : 'base demonstrativa';
  return `<div class="u-provenance-foot"><i class="ti ti-database" aria-hidden="true"></i> Fonte: ${base} · ${origin}${provenanceUpdateSuffix(updatedAtLabel)}</div>`;
}

/**
 * Rodape do Briefing que NOMEIA a origem resolvida em runtime (precedente
 * exato: buildAnalyticsProvenanceFooterMarkup) e recebe o INSTANTE da ultima
 * coleta.
 *
 * 2026-09-20: antes vinha de briefingProvenanceDateLabel(new Date()), o relogio
 * do RENDER — o rodape escrevia a data de hoje inclusive quando a origem era
 * demonstrativa, ou seja, parecia fresco com a coleta parada. Agora as duas
 * superficies vivas (esta e o Analytics) leem o mesmo frescor.
 *
 * @example
 *   buildBriefingProvenanceFooterMarkup('live', '19 set 2026, 23h20');
 *   // <div class="u-provenance-foot">… Fonte: TSE · Social Listening AtalaIA · ao vivo · atualizado 19 set 2026, 23h20</div>
 */
export function buildBriefingProvenanceFooterMarkup(
  source: ProvenanceResolvedSource,
  updatedAtLabel: string | null,
): string {
  const base = PROVENANCE_BY_SURFACE.briefing.join(' · ');
  const origin = source === 'live' ? 'ao vivo' : 'base demonstrativa';
  return `<div class="u-provenance-foot"><i class="ti ti-database" aria-hidden="true"></i> Fonte: ${base} · ${origin}${provenanceUpdateSuffix(updatedAtLabel)}</div>`;
}

/** Escalas proprietarias com nota explicativa (precedente: rep-dim-desc). */
export type ExplainedScaleKind = 'pertencimento' | 'sentimento';

/** Texto explicativo de cada escala proprietaria. */
const SCALE_NOTE_BY_KIND: Record<ExplainedScaleKind, string> = {
  pertencimento: '0–100 · índice composto de vínculo territorial (votação local, tempo de residência, participação comunitária)',
  sentimento: '−1 a +1 · média ponderada do tom das menções (24h)',
};

/**
 * Nota de escala explicada exibida junto a metricas proprietarias.
 *
 * @example
 *   buildScaleNoteMarkup('sentimento'); // <div class="u-scale-note">−1 a +1 · …</div>
 */
export function buildScaleNoteMarkup(kind: ExplainedScaleKind): string {
  return `<div class="u-scale-note">${SCALE_NOTE_BY_KIND[kind]}</div>`;
}
